import { useCallback, useMemo, useState } from 'react';
import type { QueryFunctionContext, QueryKey } from 'react-query';
import { useInfiniteQuery } from 'react-query';
import { queryKeys } from '@config/queryKeys';
import type { FilterMenuOption } from '@goodfynd/ui';
import { useScheduleApi } from '@hooks/api/useScheduleApi';
import type { ScheduleSearchResponse } from '@services/api/types';
import { findLast } from 'lodash';

import type { ScheduleSearchOptions } from '../types/schedule';

export enum ScheduleSort {
  StartAsc = '1',
  StartDesc = '2',
  EndAsc = '3',
  EndDesc = '4',
  NameAsc = '5',
  NameDesc = '6',
  PickupAddressAsc = '7',
  PickupAddressDesc = '8',
}

export function useScheduleSearch({
  onSuccess,
  queryKey,
  refetchInterval,
  refetchOnWindowFocus = false,
  request: { rowsPerPage, ...request } = {
    rowsPerPage: 6,
    sorts: ScheduleSort.StartAsc,
  },
}: ScheduleSearchOptions) {
  const [currentPage, setCurrentPage] = useState(1);
  const api = useScheduleApi();

  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    isFetched,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
    ...otherResults
  } = useInfiniteQuery<ScheduleSearchResponse, unknown>(
    queryKey ?? queryKeys.schedule.search(request),
    ({ pageParam }: QueryFunctionContext<QueryKey, number>) => {
      const page = pageParam || currentPage;
      setCurrentPage(page);
      return api.search({
        rows: rowsPerPage,
        page,
        ...request,
      });
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      getPreviousPageParam: (firstPage) => firstPage.prevCursor,
      onSuccess,
      refetchInterval,
      refetchOnWindowFocus,
    }
  );

  const { currentPageData, rows, sum } = useMemo(() => {
    const currentPageData = findLast(
      data?.pages,
      (row) => row.page === currentPage
    );
    const hasPages = (data?.pages?.length || 0) > 0;
    return {
      currentPageData,
      rows: rowsPerPage,
      sum: (hasPages && currentPageData?.stats?.total_ds?.sum) || 0,
    };
  }, [currentPage, data?.pages, rowsPerPage]);

  const total = currentPageData?.total || 0;
  const bindFacet = useCallback(
    (key: string, hasDescription?: boolean) => {
      return (currentPageData?.metadata.facet[key]?.buckets || [])
        .map((bucket) => {
          const parts = bucket.id.split('|');
          let option: FilterMenuOption = {
            count: bucket.count,
            label: parts?.[2] || bucket.id,
            value: parts?.[0] || bucket.id,
          };

          if (hasDescription) {
            option = {
              ...option,
              description: `${parts?.[1]} | ${parts?.[2]}` || bucket.id,
            };
          }

          return option;
        })
        .sort((a, b) => (a.label < b.label ? 0 : 1));
    },
    [currentPageData?.metadata.facet]
  );

  return {
    ...otherResults,
    currentPage,
    schedules: useMemo(
      () => currentPageData?.results || [],
      [currentPageData?.results]
    ),
    facets: useMemo(
      () => ({
        cities: bindFacet('city'),
        counties: bindFacet('county'),
        states: bindFacet('state'),
        postalCodes: bindFacet('postalCode'),
        organizations: bindFacet('organization', true),
        vendors: bindFacet('vendor', true),
        scheduleTypes: bindFacet('scheduleType'),
      }),
      [bindFacet]
    ),
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    isFetched,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoading || isFetchingNextPage,
    list: useMemo(
      () => currentPageData?.results || [],
      [currentPageData?.results]
    ),
    pages: Math.ceil(total / rows),
    refetch,
    rowsPerPage: rows,
    setCurrentPage,
    sum,
    total,
  };
}
